import { z } from 'zod';
import { DisplayName, Ulid } from './common.js';
/**
 * API tokens for CI (plan §7). A token is bounded to one team, optionally one app, and a
 * scope list — it publishes builds and nothing else. Membership, prefixes and other teams
 * are unreachable with one no matter who created it.
 */
export const ApiTokenScope = z.enum([
    'builds:write',
    'builds:read',
    'apps:read',
    /** Claim and report hardware verification jobs (plan §15). Runner tokens only. */
    'verify:run',
    /**
     * The game's asset library (docs/asset-library-plan.md). Held by the playground session's
     * token and nothing else, so the assistant can list and fetch what the developer uploaded.
     */
    'assets:read',
    'assets:write',
    /**
     * The store presentation: the app's name and description, the listing fields, and the icon,
     * hero, screenshots and trailers behind them (`docs/store-plan.md` §12).
     *
     * Held by the playground session's token beside the library scopes, for the same reason and
     * with the same shape: an assistant that writes a game is the thing best placed to describe
     * it, and it needs a credential to say so with. Deliberately narrower than the route it
     * reaches — `PATCH /v1/apps/:appId` also carries the slug, the archive switch, retention and
     * the two verification settings, and this scope opens **none** of them (`STORE_PRESENTATION_FIELDS`
     * in `apps.ts` is the list). Those are decisions about how a team ships, not about how a game
     * is described, and a token that could turn `holdUntilVerified` off would be a different
     * credential wearing this one's name.
     *
     * It does not publish. Making a game reachable by strangers is an owner's action and stays a
     * person's (`POST /v1/apps/:appId/store/publish` requires one), so the widest thing this scope
     * can do is finish a listing and leave it ready.
     */
    'store:write',
    /**
     * Read a game's in-game feedback (`docs/feedback-plan.md` §7) — the reports testers filed,
     * without the screenshots that came with them.
     *
     * The one scope in this list whose danger is not what it lets a caller *do*. A report's
     * `message` is free text written by a stranger who was handed a build, so a credential that
     * reads one is a credential that pulls somebody else's words into whatever is holding it.
     * `scopeCombinationError` below is the rule that follows from that, and it is enforced at
     * mint time rather than at use time — see `docs/mcp-plan.md` §3.
     */
    'feedback:read',
    /**
     * Read a game's analytics and its custom game events (`docs/game-events-plan.md` §11.4).
     *
     * **Deliberately not fenced the way `feedback:read` is**, and that is a decision (decision 9),
     * not an oversight: an event's string values are identifiers by ingest validation — no spaces, no
     * sentences — and a catalog's descriptions are written by the team, so nothing a stranger typed
     * reaches a model through it. Loosening the identifier rule in `game-events.ts` reopens this.
     */
    'analytics:read',
    /** Write a game's event catalog. A write scope, so refused beside `feedback:read`. */
    'analytics:write',
    /**
     * Read a game's achievements and leaderboards reports (`docs/achievements-plan.md` §13.7).
     *
     * **Not fenced the way `feedback:read` is**, on `analytics:read`'s argument extended (decision
     * 15): every value it returns is a number, an identifier, a pseudonym from a fixed word list, a
     * username — 3–32 of `[a-z0-9_-]` — or prose the team wrote. Nothing a stranger typed reaches a
     * model through it. A free-text player name anywhere in play services fences it that day.
     */
    'play:read',
    /**
     * Write a game's achievements and leaderboards catalog (`docs/achievements-plan.md` §13.7). A
     * write scope, so refused beside `feedback:read`.
     */
    'play:write',
    /**
     * Read a game's tickets (`docs/tickets-plan.md`) — and a ticket made from a report carries the
     * player's words, so this reads a stranger's sentences exactly as `feedback:read` does.
     *
     * **Held by the playground session's token and by nothing else**, beside that token's write
     * scopes. That is the one exception to `scopeCombinationError`'s rule, taken by the owner
     * (`docs/tickets-plan.md` §0.6, §6): inside a sandbox the credential boundary cannot hold —
     * whatever reads a ticket can read the session file — so what stands in its place is the fence,
     * the rule that the assistant works only tickets a person accepted, and a ticket surface that
     * cannot delete, publish, promote or grant. `SESSION_ONLY_API_TOKEN_SCOPES` keeps it off every
     * token a person mints.
     */
    'tickets:read',
    /** Comment on a game's tickets, tag them and move them along (§0.8). The playground's alone. */
    'tickets:write',
    /**
     * Read a game's figure sets (docs/meshy-models-architecture.md §8.2). The playground's alone,
     * beside the write scope, so the agent can see the sheets and figures it drafted.
     */
    'figures:read',
    /**
     * Draft a figure sheet, propose crops and retakes. The one spend it can cause is a concept sheet
     * on Boardaux's Meshy credits, capped per game per day; every other spend is a person's approval,
     * which no token can give. The playground's alone: no token a person mints can carry it.
     */
    'figures:write',
]);
/**
 * The scopes that change something. Everything else only reads.
 *
 * Kept as a list rather than a `endsWith(':write')` test because `verify:run` is a write by
 * every meaning that matters here and says nothing of the sort in its name.
 */
export const WRITE_API_TOKEN_SCOPES = [
    'builds:write',
    'assets:write',
    'store:write',
    'verify:run',
    'analytics:write',
    'play:write',
    'tickets:write',
    'figures:write',
];
/**
 * Scopes only the playground broker hands out (`SESSION_TOKEN_SCOPES` in `routes/playgrounds.ts`),
 * which inserts its token without going through `CreateApiTokenRequest`. Refused there, so no
 * token a person mints can carry a player's words beside a write scope (`docs/tickets-plan.md` §6).
 */
export const SESSION_ONLY_API_TOKEN_SCOPES = [
    'tickets:read',
    'tickets:write',
    'figures:read',
    'figures:write',
];
/**
 * What a CI token gets by default. `verify:run` is not included: a runner is a different
 * thing on a different machine, and giving every build pipeline the ability to claim
 * verification jobs would let one silently starve the other. `assets:*` is not included for
 * the same shape of reason: a build pipeline has no business in somebody's sprite folder, and
 * the one token that does is minted by the playground broker, which asks for it by name.
 * `store:write` is left out on the same grounds — a green CI run is not a reason to rewrite a
 * store page — but it is mintable by hand, so a team that wants its listing under version
 * control can have that without this becoming the default. `feedback:read` is not included
 * because it cannot be: see `scopeCombinationError`.
 */
export const DEFAULT_API_TOKEN_SCOPES = [
    'builds:write',
    'builds:read',
    'apps:read',
];
/**
 * **A token that reads feedback may not also write anything.**
 *
 * Feedback is the only thing this system stores that an untrusted stranger authored on purpose:
 * a tester types a sentence into a patched build and it comes back as `message`. The whole point
 * of reading it with an agent is to put that sentence in front of a model — which is exactly the
 * shape of a prompt injection, with the difference that here the attacker is invited.
 *
 * So the mitigation is not to sanitize the sentence. It is to make sure the credential that
 * carried it cannot do anything with what the sentence asks for: a token holding `feedback:read`
 * is refused at creation if it also holds a write scope, and every consumer inherits that —
 * the MCP server, a shell script, `curl`. The alternative, letting the combination exist and
 * having one careful client decline to use it, protects only the careful client.
 *
 * Read scopes are fine alongside it: `apps:read` and `builds:read` are what makes a report
 * legible ("which build was this filed against"), and neither can be turned against anybody.
 *
 * Returned as a message rather than a boolean because it is shown to whoever tried.
 */
// `analytics:read` and `play:read` are not named here on purpose: see their entries in `ApiTokenScope`.
export function scopeCombinationError(scopes) {
    if (!scopes.includes('feedback:read'))
        return null;
    const writes = scopes.filter((scope) => WRITE_API_TOKEN_SCOPES.includes(scope));
    if (writes.length === 0)
        return null;
    return (`A token with "feedback:read" cannot also hold ${writes.map((s) => `"${s}"`).join(', ')}. ` +
        'Feedback is written by testers, and a credential that reads it must not be able to act on ' +
        'what it says. Mint a second, read-only token for feedback.');
}
export const CreateApiTokenRequest = z
    .object({
    name: DisplayName,
    /** Restrict the token to a single app. Recommended for a per-repository CI credential. */
    appId: Ulid.optional(),
    scopes: z.array(ApiTokenScope).min(1).default(DEFAULT_API_TOKEN_SCOPES),
    expiresInDays: z.number().int().min(1).max(3650).optional(),
})
    .superRefine((value, ctx) => {
    const problem = scopeCombinationError(value.scopes);
    if (problem)
        ctx.addIssue({ code: 'custom', path: ['scopes'], message: problem });
    const sessionOnly = value.scopes.filter((scope) => SESSION_ONLY_API_TOKEN_SCOPES.includes(scope));
    if (sessionOnly.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['scopes'],
            message: `${sessionOnly.map((s) => `"${s}"`).join(', ')} can only be held by a playground session. ` +
                [
                    sessionOnly.some((scope) => scope.startsWith('tickets:'))
                        ? 'A ticket carries a player\'s words, and no token a person mints may read them.'
                        : null,
                    sessionOnly.some((scope) => scope.startsWith('figures:'))
                        ? 'A figure is made on BoardFlight\'s credits, and only a game\'s playground may ask for one.'
                        : null,
                ]
                    .filter(Boolean)
                    .join(' '),
        });
    }
});
/**
 * `GET /v1/token` — what the credential on this request is, answered to itself.
 *
 * Introspection and nothing more: it tells a token holder the three facts they would otherwise
 * have to be told out of band — which team it acts for, which app it is pinned to, and what it
 * may do. It reveals nothing a caller does not already hold, which is why it is the one route
 * on the token surface that requires no scope.
 *
 * It exists because an API token cannot list teams (`resolve.ts` in the CLI says why), and a
 * remote MCP client has nowhere to have been told a team id. Without this, every MCP
 * configuration would carry a hand-copied ULID beside the token.
 */
export const ApiTokenIdentity = z.object({
    tokenId: Ulid,
    teamId: Ulid,
    /** Non-null for an app-pinned token; that app is then the only one it can reach. */
    appId: Ulid.nullable(),
    scopes: z.array(ApiTokenScope),
});
export const ApiTokenSummary = z.object({
    id: Ulid,
    teamId: Ulid,
    appId: Ulid.nullable(),
    name: z.string(),
    /** Enough of the token to recognise it in a list; never enough to use it. */
    tokenPrefix: z.string(),
    scopes: z.array(z.string()),
    lastUsedAt: z.number().int().nullable(),
    expiresAt: z.number().int().nullable(),
    revokedAt: z.number().int().nullable(),
    createdAt: z.number().int(),
});
/** The secret appears in this response and nowhere else, ever. */
export const CreateApiTokenResponse = z.object({
    token: ApiTokenSummary,
    secret: z.string(),
});
//# sourceMappingURL=tokens.js.map